import { botQueryKey, useMutationDeleteBot } from "@apis:bot/bot";
import { ConfirmationDialog } from "@components:ConfirmationDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { useToasterStoreActions } from "@stores:global";
import { useTranslation } from "react-i18next";

interface BotDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  botId: string;
}

export const BotDeleteModal = ({
  isOpen,
  onClose,
  botId,
}: BotDeleteModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const mutationDeleteBot = useMutationDeleteBot({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.bots.deleted"),
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: botQueryKey._def,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.bots.deleteFailed"),
        variant: "error",
      });
    },
  });

  const handleDelete = async () => {
    await mutationDeleteBot.mutateAsync({ id: botId });
  };

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={() => onClose()}
      title={t("tenant.bots.deleteTitle")}
      description={t("tenant.bots.deleteConfirm")}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
            backgroundColor: (theme) => theme.palette.error.main,
          },
          label: t("common.delete"),
          onClick: async () => {
            await handleDelete();
          },
          disabled: !botId,
        },
      }}
    />
  );
};
